import { useRef, useEffect } from 'react';
import { mediaCache } from '@/shared/lib/mediaCache';
import type { Midia } from '@/shared/types';

export interface UseBlobMemoryManagerReturn {
  blobMapRef: React.RefObject<Map<string, string>>;
}

/** Cache de Blob URLs das mídias em exibição — evita re-download e libera
 *  RAM via URL.revokeObjectURL() quando uma mídia sai da playlist ativa. */
export function useBlobMemoryManager(midias: Midia[]): UseBlobMemoryManagerReturn {
  const blobMapRef     = useRef(new Map<string, string>());
  const oldBlobUrlsRef = useRef<string[]>([]);

  function revogarUrlsAntigas(): void {
    const pendentes = oldBlobUrlsRef.current.splice(0); // esvazia o array
    for (const url of pendentes) URL.revokeObjectURL(url);
  }

  // Cleanup total ao desmontar — libera todos os blobs ativos
  useEffect(() => {
    return () => {
      blobMapRef.current.forEach(blobUrl => oldBlobUrlsRef.current.push(blobUrl));
      blobMapRef.current.clear();
      revogarUrlsAntigas();
    };
  }, []);

  // Pré-cache: libera blobs obsoletos e gera novos para mídias adicionadas.
  // Lista vazia também passa: ela precisa revogar todos os blobs (antes um
  // refetch que zerava as mídias deixava todos vivos na RAM da TV Box).
  useEffect(() => {
    const currentUrls = new Set(midias.map(m => m.url));

    blobMapRef.current.forEach((blobUrl, origUrl) => {
      if (!currentUrls.has(origUrl)) {
        oldBlobUrlsRef.current.push(blobUrl);
        blobMapRef.current.delete(origUrl);
        mediaCache.evict([origUrl]);
      }
    });
    revogarUrlsAntigas();

    const novasUrls = [...currentUrls].filter(url => !blobMapRef.current.has(url));
    if (novasUrls.length === 0) return;

    let cancelado = false;
    (async () => {
      await mediaCache.preCache(novasUrls);
      for (const url of novasUrls) {
        if (cancelado) break;
        const blobUrl = await mediaCache.toBlobUrl(url);
        if (blobUrl === url) continue;
        // Execução cancelada no meio do await (nova lista de mídias) ou outra
        // execução já criou o blob desta URL: o blob recém-criado sobraria
        // sem dono — vídeo inteiro vazado na RAM. Revoga em vez de gravar.
        if (cancelado || blobMapRef.current.has(url)) {
          URL.revokeObjectURL(blobUrl);
          continue;
        }
        blobMapRef.current.set(url, blobUrl);
      }
    })();

    return () => { cancelado = true; };
  }, [midias]);

  return { blobMapRef };
}
